import { useCallback, useState } from 'react';
import { saveAs } from 'file-saver';
import { toast } from 'sonner';
import { openPdfInNewTab } from '@/lib/pdfViewer';

type Action = 'view' | 'download' | 'preview';

/** Thrown from a fetcher to stop quietly with a message (e.g. the form doesn't validate yet). */
export class PdfAbort extends Error {}

/**
 * View / download / preview a document PDF, with the pending state the buttons
 * need.
 *
 * Generating a PDF server-side takes a few seconds. Every detail page used to
 * call the service directly with no feedback at all, so the button sat there
 * looking broken and people clicked it again. Each action tracks its own
 * pending flag, so downloading doesn't grey out View.
 */
export function usePdfActions() {
  const [pending, setPending] = useState<Action | null>(null);

  const run = useCallback(async (action: Action, fetchBlob: () => Promise<Blob>, filename?: string) => {
    setPending(action);
    try {
      if (action === 'download') {
        saveAs(await fetchBlob(), filename || 'document.pdf');
      } else if (action === 'preview') {
        // A preview comes from an unsaved form: never navigate this tab away
        // from it. If the popup is blocked, hand the file over instead.
        await openPdfInNewTab(fetchBlob, (blob) => {
          saveAs(blob, filename || 'preview.pdf');
          toast.info('Pop-up blocked — the preview was downloaded instead.');
        });
      } else {
        await openPdfInNewTab(fetchBlob);
      }
    } catch (error) {
      if (error instanceof PdfAbort) { toast.error(error.message); return; }
      toast.error(action === 'download' ? 'Could not download the PDF' : 'Could not open the PDF');
    } finally {
      setPending(null);
    }
  }, []);

  return {
    /** Open the PDF in a new tab. */
    view: (fetchBlob: () => Promise<Blob>) => run('view', fetchBlob),
    /** Save the PDF to disk. */
    download: (fetchBlob: () => Promise<Blob>, filename: string) => run('download', fetchBlob, filename),
    /** Render an unsaved draft and open it in a new tab. */
    preview: (fetchBlob: () => Promise<Blob>, filename?: string) => run('preview', fetchBlob, filename),
    isViewing: pending === 'view',
    isDownloading: pending === 'download',
    isPreviewing: pending === 'preview',
    isBusy: pending !== null,
  };
}
